import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';

interface PopoverProps {
  /**
   * A trigger element. We clone the element to attach the ref, click handler,
   * and ARIA attributes; pass any focusable element (typically a button).
   */
  trigger: ReactElement;
  /**
   * Stable ID for the popover surface. The trigger will reference it via
   * aria-describedby so screen readers announce the popover content when
   * the trigger is focused.
   */
  describedById?: string;
  /**
   * Body of the popover. Rendered inside the surface when open.
   */
  children: ReactNode;
  /**
   * Accessible label for the popover surface itself. Defaults to "More info".
   */
  ariaLabel?: string;
}

/**
 * Hand-rolled popover primitive — no Radix. Keeps the dependency surface
 * lean and matches the visual style of Dialog/Select. Designed for inline
 * help hints: a small disclosure surface anchored next to a trigger.
 *
 * Behaviour:
 * - Click trigger to toggle. Esc closes. Click outside closes.
 * - Focus moves to the surface on open; focus returns to the trigger on close.
 * - The trigger gets `aria-describedby` so the popover content is announced.
 */
export default function Popover({
  trigger,
  describedById,
  children,
  ariaLabel = 'More info',
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const generatedId = useId();
  const surfaceId = describedById || `popover-${generatedId}`;

  // Click-outside: close when a pointer event lands outside both the surface
  // and the trigger. We use pointerdown so the close fires before any nested
  // focus or click handlers run.
  useEffect(() => {
    if (!open) return;
    const handler = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (surfaceRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [open]);

  // Esc closes from anywhere while the popover is open. Restores focus to
  // the trigger so keyboard users land where they started.
  useEffect(() => {
    if (!open) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [open]);

  // Move focus into the surface on open so screen readers and keyboard
  // users can immediately read / Tab through the content.
  useEffect(() => {
    if (open) surfaceRef.current?.focus();
  }, [open]);

  const toggle = () => {
    setOpen((prev) => {
      if (prev) {
        // Closing — restore focus to the trigger.
        queueMicrotask(() => triggerRef.current?.focus());
      }
      return !prev;
    });
  };

  // Compose handlers and ARIA wiring onto the trigger via cloneElement so
  // callers can pass any element shape (button, anchor, custom).
  const cloned = (() => {
    type TriggerProps = {
      ref?: (el: HTMLElement | null) => void;
      onClick?: (event: React.MouseEvent<HTMLElement>) => void;
      'aria-expanded'?: boolean;
      'aria-describedby'?: string;
      'aria-controls'?: string;
    };
    const existing = trigger.props as TriggerProps;
    const composed: TriggerProps = {
      ref: (el: HTMLElement | null) => {
        triggerRef.current = el;
      },
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        existing.onClick?.(event);
        if (!event.defaultPrevented) toggle();
      },
      'aria-expanded': open,
      'aria-controls': open ? surfaceId : undefined,
      'aria-describedby': open
        ? [existing['aria-describedby'], surfaceId].filter(Boolean).join(' ')
        : existing['aria-describedby'],
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { ...(trigger as any), props: { ...existing, ...composed } } as ReactElement;
  })();

  // Trap Tab inside the surface so keyboard users don't lose context. Only
  // captures Tab; everything else passes through.
  const onSurfaceKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const surface = surfaceRef.current;
    if (!surface) return;
    const focusables = surface.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea',
    );
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === surface)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <span className="popover-root">
      {cloned}
      {open && (
        <div
          ref={surfaceRef}
          id={surfaceId}
          role="dialog"
          aria-label={ariaLabel}
          tabIndex={-1}
          className="popover-surface"
          onKeyDown={onSurfaceKeyDown}
        >
          {children}
        </div>
      )}
    </span>
  );
}
